/**
 * Factory Domain Constants
 */

// ============================================================================
// Pagination
// ============================================================================

export const DEFAULT_PAGE_SIZE = 20
export const MAX_PAGE_SIZE = 100

// ============================================================================
// Status Configuration
// ============================================================================

export const FACTORY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

export type FactoryStatusType = (typeof FACTORY_STATUS)[keyof typeof FACTORY_STATUS]

export const FACTORY_STATUS_CONFIG: Record<
  FactoryStatusType,
  {
    label: string
    labelKey: string
    color: string
    bgClass: string
    textClass: string
  }
> = {
  active: {
    label: 'Active',
    labelKey: 'factories.status.active',
    color: 'green',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-600 dark:text-green-400',
  },
  inactive: {
    label: 'Inactive',
    labelKey: 'factories.status.inactive',
    color: 'gray',
    bgClass: 'bg-gray-100 dark:bg-gray-800',
    textClass: 'text-gray-600 dark:text-gray-400',
  },
}

// ============================================================================
// Form Field Names
// ============================================================================

export const FACTORY_FIELDS = {
  CODE: 'code',
  SAP_PLANT_CODE: 'sapPlantCode',
  NAME_EN: 'name.en',
  NAME_AR: 'name.ar',
  ADDRESS_LINE_1_EN: 'addressLine1.en',
  ADDRESS_LINE_1_AR: 'addressLine1.ar',
  ADDRESS_LINE_2_EN: 'addressLine2.en',
  ADDRESS_LINE_2_AR: 'addressLine2.ar',
  POSTAL_CODE: 'postalCode',
  TIMEZONE: 'timezone',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
  CITY_ID: 'cityId',
  COUNTRY_ID: 'countryId',
  STATUS: 'status',
} as const

// ============================================================================
// Translation Keys
// ============================================================================

export const T_KEYS = {
  FACTORIES: {
    TITLE: 'factories.title',
    ADD_FACTORY: 'factories.addFactory',
    EDIT_FACTORY: 'factories.editFactory',
    DELETE_FACTORY: 'factories.deleteFactory',
    FACTORY_DETAILS: 'factories.factoryDetails',
    SEARCH_PLACEHOLDER: 'factories.searchPlaceholder',
    NO_FACTORIES: 'factories.noFactories',
    ADD_DESCRIPTION: 'factories.addDescription',
    EDIT_DESCRIPTION: 'factories.editDescription',
    BASIC_INFO: 'factories.basicInfo',
    SELECT_STATUS: 'factories.selectStatus',
    LOCATION_INFO: 'factories.locationInfo',
    SELECT_LOCATION: 'factories.selectLocation',
    NO_LOCATION_OPTION: 'factories.noLocationOption',
    LOCATION_HINT: 'factories.locationHint',
    CITIES_SERVED: 'factories.citiesServed',
    CITIES_SERVED_HINT: 'factories.citiesServedHint',
    SEARCH_CITIES: 'factories.searchCities',
    NO_CITIES_MATCH: 'factories.noCitiesMatch',
    NO_CITIES_AVAILABLE: 'factories.noCitiesAvailable',
    SELECTED_CITY_COUNT: 'factories.selectedCityCount',
    SELECTED: 'factories.selected',
    LOAD_MORE_CITIES: 'factories.loadMoreCities',
    DESCRIPTION_SECTION: 'factories.descriptionSection',
    DESCRIPTION_EN: 'factories.descriptionEn',
    DESCRIPTION_AR: 'factories.descriptionAr',
    DESCRIPTION_PLACEHOLDER: 'factories.descriptionPlaceholder',
    DESCRIPTION_AR_PLACEHOLDER: 'factories.descriptionArPlaceholder',
    SAVING: 'factories.saving',
    DELETING: 'factories.deleting',
    CLOSE: 'factories.close',
    FORM: {
      CODE: 'factories.form.code',
      CODE_PLACEHOLDER: 'factories.form.codePlaceholder',
      SAP_PLANT_CODE: 'factories.form.sapPlantCode',
      SAP_PLANT_CODE_PLACEHOLDER: 'factories.form.sapPlantCodePlaceholder',
      NAME_EN: 'factories.form.nameEn',
      NAME_EN_PLACEHOLDER: 'factories.form.nameEnPlaceholder',
      NAME_AR: 'factories.form.nameAr',
      NAME_AR_PLACEHOLDER: 'factories.form.nameArPlaceholder',
      ADDRESS: 'factories.form.address',
      ADDRESS_PLACEHOLDER: 'factories.form.addressPlaceholder',
      CITY: 'factories.form.city',
      SELECT_CITY: 'factories.form.selectCity',
      COUNTRY: 'factories.form.country',
      SELECT_COUNTRY: 'factories.form.selectCountry',
      POSTAL_CODE: 'factories.form.postalCode',
      TIMEZONE: 'factories.form.timezone',
      COORDINATES: 'factories.form.coordinates',
      STATUS: 'factories.form.status',
    },
    VALIDATION: {
      CODE_REQUIRED: 'factories.validation.codeRequired',
      NAME_REQUIRED: 'factories.validation.nameRequired',
      COUNTRY_REQUIRED: 'factories.validation.countryRequired',
    },
    STATUS: {
      ACTIVE: 'factories.status.active',
      INACTIVE: 'factories.status.inactive',
    },
    MESSAGES: {
      CREATE_SUCCESS: 'factories.messages.createSuccess',
      UPDATE_SUCCESS: 'factories.messages.updateSuccess',
      DELETE_SUCCESS: 'factories.messages.deleteSuccess',
      DELETE_CONFIRM: 'factories.messages.deleteConfirm',
    },
    // Card labels
    CARD: {
      NO_ADDRESS: 'factories.card.noAddress',
      NO_CITY: 'factories.card.noCity',
      NO_COUNTRY: 'factories.card.noCountry',
      LOCATION_NOT_SET: 'factories.card.locationNotSet',
      CREATED: 'factories.card.created',
      UPDATED: 'factories.card.updated',
    },
  },
} as const
